import React from 'react';
import { Input, Select } from '@rebass/forms';
import { Skeleton } from 'components/Skeleton';
import { CURRENCIES, CURRENCY_SYMBOL } from 'utils/constants';

/**
 * One provider field: its label, and either the select or — while the provider's
 * catalog is still in flight — a skeleton of the same height. Every one of these
 * lists costs a live call to the provider (Tango Card's account list can take tens
 * of seconds), so the field holds its place instead of popping into the layout.
 *
 * Width comes from the grid track the field lands in, never from the field itself.
 */
function ProviderField({ id, label, loading, value, placeholder, options, onChange }) {
    return (
        <div>
            <div className="form-label">
                {/* No htmlFor while loading: there is no control yet to point at. */}
                {loading ? (
                    <span className="fs-13 text-secondary">{label}</span>
                ) : (
                    <label htmlFor={id} className="fs-13 text-secondary">
                        {label}
                    </label>
                )}
            </div>
            {loading ? (
                <div role="status" aria-label={`Loading ${label}`}>
                    <Skeleton className="h-8 w-full" />
                </div>
            ) : (
                <Select
                    id={id}
                    name={id}
                    value={value}
                    onChange={event => onChange(event.target.value)}
                    className="!w-full"
                >
                    <option value={''}>{placeholder}</option>
                    {options.map(option => (
                        <option key={option.id} value={option.id}>
                            {option.name}
                        </option>
                    ))}
                </Select>
            )}
        </div>
    );
}

/**
 * Which of a provider's two selects the section shows, and whether their catalogs
 * are still loading. A catalog is "loading" while the provider has no entry in the
 * options map at all — the page writes each provider's key as its request lands.
 *
 * @param {Object} provider Provider manifest entry
 * @param {Object} targetOptions provider machine name => rewards [{id, name}]
 * @param {Object} fundingAccountOptions provider machine name => funding accounts [{id, name}]
 * @returns {Object} the provider, its option lists, and per-select loading/visibility flags
 */
function describeProviderFields(provider, targetOptions, fundingAccountOptions) {
    const targets = targetOptions[provider.name];
    const fundingAccounts = fundingAccountOptions[provider.name];
    const targetsLoading = targets === undefined;
    // Only providers that hold several named balances are asked for accounts, so
    // for every other provider a missing entry is the final answer, not a pending one.
    const fundingAccountsLoading = Boolean(provider.funding_account_per_study) && fundingAccounts === undefined;

    return {
        provider,
        targets: targets || [],
        fundingAccounts: fundingAccounts || [],
        targetsLoading,
        fundingAccountsLoading,
        /*
            Master parity: the reward select only renders when the catalog is
            non-empty. Nothing for an empty catalog. A failed fetch also lands
            here, which conflates it with "empty" — see the skipped BUG test
            [PFOX-1174].
        */
        // Guarded like the two reads around it, and like master's
        // `tremendous_campaigns && .length > 0`: targetsLoading only short-circuits
        // `undefined`, so any other falsy body — a null from the targets endpoint,
        // which loadIncentiveOptions stores verbatim — would throw here and take out
        // the whole Study Settings render rather than one field.
        showTarget: targetsLoading || (targets || []).length > 0,
        // With one account the API pays from it, so there is no choice to make;
        // with none there is nothing to show.
        showFundingAccount: fundingAccountsLoading || (fundingAccounts || []).length > 1
    };
}

/**
 * The study's incentive: what each participant is paid, and — per connected
 * incentive provider — which reward and which funding account it is paid from.
 *
 * The provider fields are indented under the amount because they qualify it
 * rather than stand beside it, and they appear only for providers the account has
 * connected: no integrations means no indented group at all. The indent idiom
 * (`border-l pl-4` + `fs-13 text-secondary` labels) is shared with the account
 * settings page's Fatigue subsection.
 *
 * Each provider gets its own `grid-cols-2` row rather than flexed halves: the
 * column tracks are then identical in every row, so a row holding a single field
 * (a campaign) lines its select up with a row holding two (reward + funding
 * account). Under flex the pair shrank by the gap while the lone field did not,
 * and the columns drifted. grid-cols-2's minmax(0, 1fr) also keeps a long option
 * ("Amazon.com — Amazon eGift Card") from widening its own track past half the row.
 *
 * Manifest-driven: no provider is named here.
 *
 * @param {string} incentiveCurrency ISO currency code the study pays in
 * @param {number|string} incentive Amount each participant is paid
 * @param {Function} onIncentiveChange Change handler for the currency/amount fields, by input name
 * @param {Array} providers Connected providers from the incentive manifest
 * @param {Object} targetOptions provider machine name => rewards [{id, name}]
 * @param {Object} fundingAccountOptions provider machine name => funding accounts [{id, name}]
 * @param {Object} targets provider machine name => selected reward id
 * @param {Object} fundingAccounts provider machine name => selected funding account id
 * @param {boolean} providersFailed Whether the manifest itself could not be loaded
 * @param {Function} onTargetChange (providerName, rewardId) => void
 * @param {Function} onFundingAccountChange (providerName, fundingAccountId) => void
 */
export function StudyIncentiveSettings({
    incentiveCurrency,
    incentive,
    onIncentiveChange,
    providers,
    targetOptions,
    fundingAccountOptions,
    targets,
    fundingAccounts,
    providersFailed,
    onTargetChange,
    onFundingAccountChange
}) {
    const providerFields = providers
        .map(provider => describeProviderFields(provider, targetOptions, fundingAccountOptions))
        .filter(fields => fields.showTarget || fields.showFundingAccount);

    return (
        <div className="form-row-margin">
            <div className="form-label">
                <label htmlFor="incentive">Incentive</label>
            </div>
            {/* Narrow, so the amount reads as one short phrase instead of two wide columns. */}
            <div className="flex gap-3">
                <div className="w-28">
                    <Select
                        name="incentive_currency"
                        value={incentiveCurrency}
                        onChange={onIncentiveChange}
                        aria-label="Incentive currency"
                        className="!w-full"
                    >
                        {CURRENCIES.map(currency => (
                            <option key={currency} value={currency}>
                                {CURRENCY_SYMBOL[currency]} ({currency})
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-28">
                    <Input
                        id="incentive"
                        type="number"
                        name="incentive"
                        onChange={onIncentiveChange}
                        required
                        value={incentive}
                    />
                </div>
            </div>

            {providersFailed && (
                <div role="alert" className="error border p-4 mt-4">
                    We could not load your connected incentive providers, so reward selection is unavailable. Reload the
                    page to try again.
                </div>
            )}

            {providerFields.length > 0 && (
                <div className="border-l pl-4 mt-4 grid gap-4">
                    {providerFields.map(fields => (
                        <div
                            key={fields.provider.name}
                            className="grid grid-cols-2 gap-6"
                            data-testid={`incentive-provider-${fields.provider.name}`}
                        >
                            {fields.showTarget && (
                                <ProviderField
                                    id={`incentive_target_${fields.provider.name}`}
                                    label={`${fields.provider.label} ${fields.provider.target_label}`}
                                    loading={fields.targetsLoading}
                                    value={targets[fields.provider.name] || ''}
                                    placeholder="None"
                                    options={fields.targets}
                                    onChange={targetId => onTargetChange(fields.provider.name, targetId)}
                                />
                            )}
                            {fields.showFundingAccount && (
                                <ProviderField
                                    id={`incentive_funding_account_${fields.provider.name}`}
                                    label={`${fields.provider.label} Funding account`}
                                    loading={fields.fundingAccountsLoading}
                                    value={fundingAccounts[fields.provider.name] || ''}
                                    placeholder="Select a funding account"
                                    options={fields.fundingAccounts}
                                    onChange={accountId => onFundingAccountChange(fields.provider.name, accountId)}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
